export interface FormattedMessage extends MessageDTO{
  splitMessageText: string[];
}

export const useMessageStore = defineStore('Message', () => {
  const messageAPI = useMessageAPI();
  const { openToast } = useUserMessages();
  const { t } = useEkI18n();

  const messages = ref<MessageDTO[]>([]);

  // Wrapped in try catch due to a error that might be related to this call failing
  async function fetchMessages(fitterId: number) {
    try {
      const response = await messageAPI.fetchMessagesForFitter(fitterId);

      if (response?.HasErrors === false) {
        messages.value = response.Data;

        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  async function markAsRead(messageId: number) {
    const response = await messageAPI.markMessageAsRead(messageId);

    if (response !== undefined && response.HasErrors === false) {
      const message = messages.value.find(message => message.Id === messageId);

      if (message !== undefined) {
        message.IsRead = true;
      }
    }
  }

  async function markAllAsRead(fitterId: number) {
    const response = await messageAPI.markAllUnreadAsRead(fitterId);

    if (response?.HasErrors === false) {
      messages.value.forEach((message) => {
        message.IsRead = true; 
      });
    }
  }

  async function deleteMessage(messageId: number) {
    const response = await messageAPI.deleteMessage(messageId);

    if (response !== undefined && response.HasErrors === false) {
      const deletedIndex = messages.value.findIndex(message => message.Id === messageId);
      openToast(t('messages.deleteSuccess'), ToastType.SUCCESS);

      if (deletedIndex !== -1) {
        messages.value.splice(deletedIndex, 1);
      }
    }
  }

  // Splits the messageText into text and links with text on even indexes and links on odd indexes
  const formattedMessages = computed<FormattedMessage[]>(() => messages.value.map((message) => {
    const formattedMessage: FormattedMessage = {
      ...message,
      splitMessageText: message.MessageText.split(/(\S+:\/\/\S+\.\S+)/)
    };

    return formattedMessage;
  }));

  const { query: searchQuery, results: searchResults } = useSearch(formattedMessages, ['MessageText', 'Timestamp', message => message.Project?.Name, message => message.Project?.Reference]);

  const unreadMessageCount = computed(() => messages.value.filter(message => !message.IsRead).length);

  return {
    _messages: messages,
    unreadMessageCount,
    searchQuery,
    searchResults,
    fetchMessages,
    markAsRead,
    markAllAsRead,
    deleteMessage
  };
});
